import type { SelectStore } from '@ariakit/react';
import {
  Select as AriaSelect,
  Combobox,
  SelectItem,
  SelectLabel,
  SelectPopover,
} from '@ariakit/react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css } from 'styled-components';
import type { ITheme } from '../../../utils/theme';
import Icon from '../../icon/icon';
import { InlineTextButton } from '../../inline-text-button/inline-text-button';
import Typography from '../../typography/typography';
import { getStyledAriaSelectBorder } from '../helper-functions/getStyledAriaSelectBorder';
import { getStyledAriaSelectBorderRadius } from '../helper-functions/getStyledAriaSelectBorderRadius';
import { getStyledAriaSelectColor } from '../helper-functions/getStyledAriaSelectColor';
import { getStyledSelectLabelColor } from '../helper-functions/getStyledSelectLabelColor';
import { getStyledSelectPopoverTop } from '../helper-functions/getStyledSelectPopoverTop';

interface IStyledSelectLabel {
  theme: ITheme;
  $hasError: boolean;
}

export const SelectWrapper = styled.div`
  ${({ theme, width }: { theme: ITheme; width?: string }) => css`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: initial;
    justify-content: flex-start;
    width: ${width ?? '100%'};
    max-width: ${width ?? theme.input.text.width};
    position: relative;

    @media (max-width: ${theme.breakpoints?.sm ?? '600px'}) {
      max-width: 100%;
    }
  `}
`;

const SelectLabelTypography = styled(SelectLabel)(
  ({ theme }) => theme.typography.bodySm2
);

export const StyledMessageTypography = styled(Typography)`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(0.875)};
    text-transform: none;
    text-align: left;
  `}
`;

export const StyledSelectLabel = styled(SelectLabelTypography)`
  ${({ $hasError, theme }: IStyledSelectLabel) => css`
    text-transform: none;
    ::first-letter {
      text-transform: uppercase;
    }
    color: ${getStyledSelectLabelColor($hasError, theme)};
  `}
`;
export const SearchIconSpan = styled.span`
  ${({ $isCreateButtonVisible }: { $isCreateButtonVisible: boolean }) => css`
    position: absolute;
    right: ${$isCreateButtonVisible ? '70px' : '16px'};
    top: 17px;
  `};
`;
export const CreateButton = styled(InlineTextButton)`
  ${({ theme }) => css`
    ${theme.typography.bodyMd2};
    position: absolute;
    right: 24px;
    top: 22px;
  `}
`;
const StyledTypographyCombobox = styled(Combobox)(
  ({ theme }) => theme.typography.bodyMd1
);
export const StyledCombobox = styled(StyledTypographyCombobox)`
  ${({
    theme,
    $hasError,
    $canCreateOption,
  }: {
    theme: ITheme;
    $hasError: boolean;
    $canCreateOption: boolean;
  }) => css`
    width: calc(100% - ${theme.spacing(2)});
    height: ${theme.input?.text?.height ?? '38px'};
    margin-left: ${theme.spacing(1)};
    border-radius: ${theme.borderRadius.sm};
    background: ${theme.palette.neutral['00']};
    color: ${$hasError
      ? theme.palette.error.main
      : theme.palette.neutral['100']};
    border: 1px solid
      ${$hasError ? theme.palette.error.main : theme.palette.neutral['80']};
    padding: ${theme.spacing(1, 1.5)};
    padding-right: ${$canCreateOption ? '90px' : '30px'};

    margin-bottom: ${theme.spacing(1)};
    margin-top: ${theme.spacing(1.25)};
    position: relative;

    &:focus {
      box-sizing: border-box;
      outline: 2px solid
        ${$hasError ? theme.palette.error.main : theme.palette.neutral['100']};
    }
    @media (max-width: ${theme.breakpoints.sm}) {
      // font size of 16px on mobile to avoid zoom
      font-size: 16px;
    }
  `};
`;

export const StyledAriaSelect = styled(AriaSelect)`
  ${({
    theme,
    store,
    $isTop,
    $isDirty,
    $hasError,
    width,
    $hasRightDivider,
  }: {
    theme: ITheme;
    store: SelectStore;
    $isTop: boolean;
    $isDirty: boolean;
    $hasError: boolean;
    width?: string;
    $hasRightDivider: boolean;
  }) => {
    const open = store.useState('open');
    const borderRadius = getStyledAriaSelectBorderRadius(open, $isTop, theme);
    return css`
      box-sizing: border-box;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: ${width ?? theme?.input?.text?.width ?? '335px'};
      max-width: ${width ?? theme?.input?.text?.width ?? '335px'};
      min-height: ${theme?.input?.text?.height ?? '180px'};
      height: ${open ? '100%' : (theme?.input?.text?.height ?? '180px')};
      padding: ${theme.spacing('7px', 2, '7px', 1.5)} !important;
      border-radius: ${borderRadius};
      background: ${theme.palette.neutral['00']};
      ${theme.typography.bodyMd1};
      font-weight: ${$isDirty ? '500' : '400'};
      color: ${getStyledAriaSelectColor($isDirty, $hasError, theme)};
      ${getStyledAriaSelectBorder(open, $hasError, $hasRightDivider, theme)};
      ${open && $isTop && 'border-top: 1px solid transparent;'}
      ${open && !$isTop && 'border-bottom: 1px solid transparent;'}
      transition: background 0.2s ease-in-out;
      position: relative;
      border: 0.5px solid ${
        $hasError ? theme.palette.error.main : theme.palette.neutral['90']
      };
      &:focus-visible {
        box-sizing: border-box;
        outline 2px solid
          ${
            $hasError ? theme.palette.error.main : theme.palette.neutral['100']
          };
        padding-top: 7px;
        padding-left: 15px;
        padding-right: 15px;
      }

      &:disabled {
        background: ${theme.palette.neutral['50']};
        color: ${theme.palette.neutral['70']};
      }

      @media (max-width: ${theme.breakpoints?.sm ?? '600px'}) {
        max-width: 100%;
      }
      ${
        $hasRightDivider &&
        `
          :before {
            content: '';
            position: absolute;
            right: 0;
            bottom: ${theme.spacing(1)};
            top: 8px;
            width: 1px;
            height: 20px;
            background-color: ${theme.palette.neutral['60']};
          }`
      }
    `;
  }}
`;
export const StyledSelectPopover: StyledComponent<
  typeof SelectPopover,
  DefaultTheme,
  Record<string, unknown>,
  never
> = styled(SelectPopover)`
  ${({
    theme,
    $showSearch,
    width,
    maxHeight,
    $isTop,
    $hasError,
  }: {
    theme: ITheme;
    $showSearch: boolean;
    width?: string;
    maxHeight?: string;
    $isTop?: boolean;
    $hasError: boolean;
  }) => {
    const top = getStyledSelectPopoverTop($showSearch, $isTop ?? false);
    return css`
      box-sizing: border-box;
      background: ${theme.palette.neutral['00']};
      right: 0;
      transform: none;
      border: 0.5px solid ${
        $hasError ? theme.palette.error.main : theme.palette.neutral['90']
      };
      ${$isTop ? `border-bottom: none;` : `border-top: none;`}
      width: ${width ?? '100%'};
      max-width: ${width ?? theme.input?.text?.width ?? '335px'};
      max-height: ${maxHeight ?? 'min(var(--popover-available-height, 300px), 320px)'};
      z-index: 5000;
      border-radius: ${
        $isTop
          ? `${theme.borderRadius.sm} ${theme.borderRadius.sm} 0px 0px`
          : `0px 0px ${theme.borderRadius.sm} ${theme.borderRadius.sm}`
      };
      overflow: auto;
      overscroll-behavior: contain;
      overflow-x: hidden;
      top: ${top};
      font-weight: ${theme.typography.bodyMd1.fontWeight};
      color: ${theme.palette.neutral['100']};

      .select-option[data-active-item] {
        background: ${theme.palette.neutral['50']};
        font-weight: ${theme.typography.bodyLg1.fontWeight};
      }
      .select-option-filter[data-active-item] {
        background: ${theme.palette.neutral['50']};
        font-weight: ${theme.typography.bodyLg1.fontWeight};
      }
      .select-option[aria-selected='true'] {
        &.select-option[data-active-item] {
          font-weight: ${theme.typography.bodyLg1.fontWeight};
        }
      }
      .select-option[aria-selected='true'] {
        font-weight: ${theme.typography.bodyLg2.fontWeight};
      }
      .select-option[data-autofocus='true'] {
        font-weight: ${theme.typography.bodyLg2.fontWeight} !important;
      }

      @media (max-width: ${theme.breakpoints?.sm ?? '600px'}) {
        max-width: 100%;
      }

      /* Hide scrollbar in Chrome, Safari and Opera */
      ::-webkit-scrollbar {
        width: 5px; /* Width of the scrollbar */
        height: 5px; /* Height of the scrollbar */
      }

      /* Style the scrollbar thumb */
      ::-webkit-scrollbar-thumb {
        background-color: ${
          theme.palette.neutral[60]
        }; /* Color of the scrollbar thumb */
        border-radius: 5px; /* Radius of the scrollbar thumb */
        border: none; /* Border of the scrollbar thumb */
      }

      /* Hide the content in the padding section of the scrollbar */
      ::-webkit-scrollbar-track {
        background-color: transparent /* Background color of the padding section */
        border-radius: 5px; /* Radius of the padding section */
        border: none; /* Border of the padding section */
      }
    `;
  }}
`;
export const SelectOption: StyledComponent<
  typeof SelectItem,
  DefaultTheme,
  Record<string, unknown>,
  never
> = styled(SelectItem)`
  ${({ theme, width }: { theme: ITheme; width?: string }) => css`
    box-sizing: border-box;
    padding: ${theme.spacing(1, 0, 1, 1)};
    width: 100%;
    max-width: ${width ?? theme?.input?.text?.width ?? '335px'};
    min-height: ${theme?.input?.text?.height ?? '38px'};
    font-family: ${theme.fontFamily};
    color: ${theme.palette.neutral['100']};
    font-size: 17px;
    line-height: 22px;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: wrap;
    border-top: 1px solid ${theme.palette.neutral[50]};
    ${theme.typography.bodyMd1};
    cursor: pointer;

    @media (max-width: ${theme.breakpoints?.sm ?? '600px'}) {
      max-width: 100%;
    }
  `}
`;

export const NoResultsFound = styled.div`
  ${({ theme, width }: { theme: ITheme; width?: string }) => css`
    box-sizing: border-box;
    padding: ${theme.spacing(1.5)};
    width: ${width ?? '100%'};
    max-width: ${width ?? theme?.input?.text?.width ?? '335px'};
    min-height: ${theme?.input?.text?.height ?? '48px'};
    font-family: ${theme.fontFamily};
    color: ${theme.palette.neutral['100']};
    font-size: 17px;
    line-height: 22px;
    outline: none !important;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${theme.typography.bodyMd1};

    @media (max-width: ${theme.breakpoints?.sm ?? '600px'}) {
      max-width: 100%;
    }
  `}
`;

export const LabelWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding-bottom: ${theme.spacing(1)};
  `}
`;

export const InfoIcon = styled(Icon)`
  line-height: 12px;
  height: 12px;
  display: inline-block;
`;

export const HiddenCombobox = styled(Combobox)`
  display: none;
`;

export const CreationErrorWrapper = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing(0, 1)};
  `}
`;
